import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanLine, X, Loader2, Keyboard, Camera } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { detectDeviceType } from "../lib/device";

/** Barcode / QR lookup trigger.
 *
 *  Device-aware routing:
 *  – Desktop  → "Ready to Scan" hardware-scanner mode. No camera / no webcam
 *    permission prompt. An always-focused input captures a USB/Bluetooth
 *    scanner gun's rapid keystrokes and auto-submits on Enter. Manual typing
 *    and paste both work in the same field.
 *  – Tablet / Mobile → live camera scan via html5-qrcode with safe defaults
 *    (`{ facingMode: { ideal: "environment" } }` only — no forced resolutions
 *    or focus modes that cause the stream to fail on some devices). The
 *    underlying <video> is patched to `autoplay + playsInline + muted` so it
 *    starts instantly on iOS/Android without a tap.
 *
 *  Every mode also exposes a manual fallback so a scan can always be typed in.
 */
export default function ScanTrigger({ compact = false, inline = false }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("choose"); // choose | camera | hardware | manual
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [cameraError, setCameraError] = useState("");
  // Guard so a scanner-gun's rapid Enter or a camera's duplicate decode fires
  // navigation exactly once per scan.
  const consumed = useRef(false);
  const scannerRef = useRef(null);
  const scannerElId = "vs-qr-reader";
  const nav = useNavigate();

  // Pick the right initial mode as soon as the modal opens.
  const openScanner = () => {
    const dt = detectDeviceType();
    setMode(dt === "desktop" ? "hardware" : "camera");
    setOpen(true);
  };

  // ------- Camera lifecycle (mobile + tablet) -------
  useEffect(() => {
    if (mode !== "camera" || !open) return;
    consumed.current = false;
    setCameraError("");
    let alive = true;
    let videoPatchTimer = null;
    (async () => {
      try {
        const { Html5Qrcode } = await import("html5-qrcode");
        const el = document.getElementById(scannerElId);
        if (!el) return;
        scannerRef.current = new Html5Qrcode(scannerElId, /* verbose */ false);
        // html5-qrcode's start() validates facingMode strictly — it accepts
        // either a plain string ("environment" | "user") OR an object with an
        // `exact` key. Passing `{ ideal: "environment" }` was throwing
        // "facingmode should be string or object with exact as key" on iPads
        // and Android phones. We use the plain string and let the browser
        // fall back to the front camera if the back one is unavailable.
        await scannerRef.current.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: (vw, vh) => {
              const short = Math.min(vw, vh);
              const long  = Math.max(vw, vh);
              return {
                width:  Math.round(Math.min(long * 0.7, 480)),
                height: Math.round(Math.min(short * 0.55, 220)),
              };
            },
            aspectRatio: 1.7777,
            experimentalFeatures: { useBarCodeDetectorIfSupported: true },
          },
          (decoded) => {
            if (!alive || consumed.current) return;
            consumed.current = true;
            handleFound(decoded);
          },
          () => {}
        );
        // html5-qrcode creates the <video> internally on first frame. Patch
        // its attributes so the stream starts instantly on iOS/Android WebViews
        // that block autoplay without a user gesture on plain <video>.
        videoPatchTimer = setInterval(() => {
          const v = el.querySelector("video");
          if (v) {
            v.autoplay = true;
            v.muted = true;
            v.setAttribute("playsinline", "true");
            v.setAttribute("webkit-playsinline", "true");
            v.play?.().catch(() => {});
            clearInterval(videoPatchTimer);
            videoPatchTimer = null;
          }
        }, 100);
        setTimeout(() => { if (videoPatchTimer) { clearInterval(videoPatchTimer); videoPatchTimer = null; } }, 3000);
      } catch (e) {
        setCameraError(e?.message || String(e) || "Camera unavailable");
      }
    })();
    return () => {
      alive = false;
      if (videoPatchTimer) clearInterval(videoPatchTimer);
      // Clean teardown — stop the stream so the browser's camera indicator
      // switches off immediately and the device isn't left in a "camera stuck"
      // state after closing the popup.
      const s = scannerRef.current;
      scannerRef.current = null;
      if (s) {
        (async () => {
          try { if (s.isScanning) await s.stop(); } catch (err) { console.debug("scanner stop error", err); }
          try { s.clear(); } catch (err) { console.debug("scanner clear error", err); }
        })();
      }
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [mode, open]);

  const handleFound = async (raw) => {
    const c = (raw || "").trim().toUpperCase();
    if (!c) return;
    setBusy(true);
    try {
      const r = await api.get(`/lots/lookup/${encodeURIComponent(c)}`);
      toast.success(`Found ${r.data.color_name} · Rack ${r.data.rack_no}`);
      closeAll();
      nav(`/lot/${r.data.id}`);
    } catch (err) {
      // Reset the guard so the operator can rescan without reopening the modal.
      consumed.current = false;
      setCode("");
      toast.error(err.response?.data?.detail || "No lot found for that code");
    } finally { setBusy(false); }
  };

  const submitCode = (e) => { e?.preventDefault?.(); handleFound(code); };
  const closeAll = () => {
    setOpen(false); setMode("choose"); setCode("");
    setCameraError(""); consumed.current = false;
  };

  return (
    <>
      {compact ? (
        <button onClick={openScanner} data-testid="scan-trigger-compact"
          className="p-2 -mr-2 text-[#2D2622]" aria-label="Scan barcode">
          <ScanLine size={20} strokeWidth={1.7}/>
        </button>
      ) : inline ? (
        <button onClick={openScanner} data-testid="scan-trigger-inline"
          className="w-full h-full px-4 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#2D2622]">
          <ScanLine size={14}/> Scan barcode
        </button>
      ) : (
        <button onClick={openScanner} data-testid="scan-trigger"
          className="w-full flex items-center gap-2 px-3 py-2.5 bg-[#F3ECDF] border border-[#E5D8C4] hover:border-[#2D2622] transition-colors text-sm font-medium text-[#2D2622]">
          <ScanLine size={16} strokeWidth={1.7}/> <span>Scan barcode</span>
        </button>
      )}
      {open && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
          onClick={closeAll}>
          <div className="bg-white w-full max-w-md border border-[#E5D8C4]" onClick={(e)=>e.stopPropagation()}>
            <div className="p-4 border-b border-[#F3ECDF] flex justify-between items-start">
              <div>
                <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold">Barcode Lookup</div>
                <div className="font-display font-bold text-lg text-[#2D2622] mt-1">
                  {mode === "hardware" && "Ready to Scan"}
                  {mode === "manual"   && "Type code manually"}
                  {mode === "camera"   && "Point camera at label"}
                </div>
              </div>
              <button onClick={closeAll} data-testid="scan-close" className="p-1 text-[#8B6F47]"><X size={16}/></button>
            </div>

            {/* Desktop / hardware scanner-gun mode ---------------------------- */}
            {mode === "hardware" && (
              <form onSubmit={submitCode} className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold">
                  <ScanLine size={14}/> Awaiting barcode scanner…
                </div>
                <input autoFocus value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  data-testid="scan-hardware-input" autoCapitalize="characters"
                  autoCorrect="off" spellCheck="false"
                  placeholder="Scan or type barcode · press Enter"
                  className="w-full h-16 px-4 border-2 border-[#2D2622] focus:border-[#5D4A2E] focus:outline-none font-mono2 text-2xl tracking-wider text-center uppercase" />
                <p className="text-[11px] text-[#8B6F47] text-center">
                  Point your USB / Bluetooth scanner at the label · we listen for its
                  rapid input and Enter key. You can also type or paste a code here.
                </p>
                <div className="flex gap-2">
                  <button type="submit" disabled={busy || !code.trim()} data-testid="scan-hardware-submit"
                    className="flex-1 h-11 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] disabled:opacity-40">
                    {busy ? <Loader2 className="animate-spin inline" size={14}/> : "Find lot"}
                  </button>
                </div>
              </form>
            )}

            {/* Camera mode ---------------------------------------------------- */}
            {mode === "camera" && (
              <div className="p-4 space-y-3">
                <div className="vs-scan-wrap relative w-full aspect-video bg-black overflow-hidden"
                     data-testid="scan-camera-view">
                  <div id={scannerElId} className="w-full h-full" />
                  <div className="vs-scan-reticle" aria-hidden="true">
                    <div className="box">
                      <span className="corner tl" />
                      <span className="corner tr" />
                      <span className="corner bl" />
                      <span className="corner br" />
                      <span className="laser" />
                    </div>
                  </div>
                </div>
                {cameraError && (
                  <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 px-3 py-2"
                       data-testid="scan-camera-error">
                    Camera error: {cameraError}
                  </div>
                )}
                <p className="text-[11px] text-[#8B6F47] text-center">
                  Align the barcode inside the yellow frame · hold steady for a beat
                </p>
                <button type="button" onClick={() => setMode("manual")}
                  data-testid="scan-switch-manual"
                  className="w-full h-11 border border-[#E5D8C4] text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2 hover:border-[#2D2622]">
                  <Keyboard size={13}/> Type code manually instead
                </button>
              </div>
            )}

            {/* Manual fallback (accessible from camera mode) ------------------ */}
            {mode === "manual" && (
              <form onSubmit={submitCode} className="p-4 space-y-3">
                <input autoFocus value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  data-testid="scan-manual-input" autoCapitalize="characters"
                  autoCorrect="off" spellCheck="false"
                  placeholder="VS-ABCD1234"
                  className="w-full h-14 px-4 border border-[#E5D8C4] focus:border-[#2D2622] focus:outline-none font-mono2 text-xl tracking-wider text-center uppercase" />
                <p className="text-[11px] text-[#8B6F47] text-center">Auto-uppercases · Enter to look up</p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setMode("camera")}
                    data-testid="scan-switch-camera"
                    className="flex-1 h-11 border border-[#E5D8C4] text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2">
                    <Camera size={13}/> Camera
                  </button>
                  <button type="submit" disabled={busy || !code.trim()} data-testid="scan-manual-submit"
                    className="flex-1 h-11 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em] disabled:opacity-40">
                    {busy ? <Loader2 className="animate-spin inline" size={14}/> : "Find lot"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
